import axios from 'axios';
import type {
  AutomationListParams,
  ExecutionListParams,
  ExecutionLogsParams,
  CreateAutomationInput,
  SendVerificationCodeRequest,
  VerifyCodeRequest,
  CreatePaymentPreferenceRequest,
  CreateCustomPaymentLinkRequest,
  CreateCustomPaymentPreferenceRequest,
  UpdateCustomPaymentLinkRequest,
  ListCustomPaymentLinksFilters,
  GetAccessesParams,
} from '@/shared/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // CRITICAL: Send HttpOnly cookies with all requests
});

// NOTE: Token interceptor is configured in AuthContext to avoid duplication
// and to properly handle token refresh logic
// Tokens are stored in HttpOnly cookies for security, and also in localStorage as fallback

// ==========================================
// API Methods
// ==========================================

// Automations
export const automationsAPI = {
  // OTIMIZADO - Usa endpoint agregado com cache
  listAggregated: (params?: { isActive?: boolean }) =>
    api.get('/automations/stats/aggregated', { params }),

  list: (params?: AutomationListParams) => api.get('/automations', { params }),

  getById: (id: string) => api.get(`/automations/${id}`),

  create: (data: CreateAutomationInput) => api.post('/automations', data),

  update: (id: string, data: Partial<CreateAutomationInput>) =>
    api.patch(`/automations/${id}`, data),

  toggle: (id: string) => api.patch(`/automations/${id}/toggle`),

  execute: (id: string, params?: Record<string, unknown>) =>
    api.post(`/automations/${id}/execute`, { params }),

  delete: (id: string) => api.delete(`/automations/${id}`),

  // Jobs
  listJobs: () => api.get('/automations/jobs'),

  getJobById: (jobId: string) => api.get(`/automations/jobs/${jobId}`),
};

// Executions
export const executionsAPI = {
  // OTIMIZADO - Busca todas as stats do dashboard em 1 request
  getDashboardStats: () => api.get('/executions/dashboard/stats'),

  list: (params?: ExecutionListParams) => api.get('/executions', { params }),

  recent: (limit?: number) => api.get('/executions/recent', { params: { limit } }),

  stats: (params?: { automationId?: string; startDate?: string; endDate?: string }) =>
    api.get('/executions/stats', { params }),

  activity: () => api.get('/executions/activity'),

  timeSavedTrend: (months?: number) =>
    api.get('/executions/time-saved-trend', { params: { months } }),

  getById: (id: string) => api.get(`/executions/${id}`),

  getLogs: (id: string, params?: ExecutionLogsParams) =>
    api.get(`/executions/${id}/logs`, { params }),

  cancel: (id: string) => api.post(`/executions/${id}/cancel`),

  retry: (id: string) => api.post(`/executions/${id}/retry`),
};

// Health
export const healthAPI = {
  check: () => api.get('/health', { baseURL: API_URL }),
};

// WhatsApp
export const whatsappAPI = {
  getAllInstances: () => api.get('/whatsapp/instances'),
  connectInstance: (
    instance: 'OPERACIONAL_ROBO' | 'OPERACIONAL_SAULO' | 'COMERCIAL_GABRIEL' | 'ADMIN' | 'PROMOCOES',
    phone?: string
  ) => api.post(`/whatsapp/instances/${instance}/connect`, phone ? { phone } : {}),
};

// Auth API (NEW)
export const authAPI = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),

  register: (email: string, password: string, name: string) =>
    api.post('/auth/register', { email, password, name }),

  logout: () => api.post('/auth/logout'),

  me: () => api.get('/auth/me'),

  refresh: (refreshToken: string) => api.post('/auth/refresh', { refreshToken }),

  changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword, confirmPassword }),
};

// Verification API
export const verificationAPI = {
  sendCode: (data: SendVerificationCodeRequest) => api.post('/verification/send-code', data),

  verifyCode: (data: VerifyCodeRequest) => api.post('/verification/verify-code', data),
};

// Payment API
export const paymentAPI = {
  createPreference: (data: CreatePaymentPreferenceRequest) =>
    api.post('/payment/create-preference', data),
};

// Custom Payment Link API
export const customPaymentLinkAPI = {
  // Admin endpoints
  create: (data: CreateCustomPaymentLinkRequest) => api.post('/payment/custom/create', data),

  list: (filters?: ListCustomPaymentLinksFilters, pagination?: { skip?: number; take?: number }) =>
    api.get('/payment/custom/list', { params: { ...filters, ...pagination } }),

  update: (token: string, data: UpdateCustomPaymentLinkRequest) =>
    api.patch(`/payment/custom/${token}`, data),

  deactivate: (token: string) => api.post(`/payment/custom/${token}/deactivate`),

  delete: (token: string) => api.delete(`/payment/custom/${token}`),

  getDetails: (token: string) => api.get(`/payment/custom/${token}/details`),

  // Public endpoints
  validate: (token: string) => api.get(`/payment/custom/validate/${token}`),

  createPreference: (data: CreateCustomPaymentPreferenceRequest) =>
    api.post('/payment/custom/create-preference', data),
};

// Client API
export const clientAPI = {
  getDashboard: () => api.get('/client/dashboard'),
  sync: () => api.post('/client/sync'),
  getEmissions: () => api.get('/client/emissoes'),
  getNextTrip: () => api.get('/client/proxima-viagem'),

  // Loyalty Partners
  getLoyaltyPartners: () => api.get('/loyalty-partners'),

  // Favorite Partners
  getFavoritePartners: () => api.get('/customer/favorite-partners'),
  addFavoritePartner: (partnerId: string) =>
    api.post('/customer/favorite-partners', { partnerId }),
  removeFavoritePartner: (partnerId: string) =>
    api.delete(`/customer/favorite-partners/${partnerId}`),
};

// Users API
export const usersAPI = {
  list: () => api.get('/users'),

  listClients: () => api.get('/clients'),

  getById: (id: string) => api.get(`/users/${id}`),

  create: (data: {
    email: string;
    password: string;
    name: string;
    role?: string;
    notionPageId?: string;
    isActive?: boolean;
  }) => api.post('/users', data),

  update: (
    id: string,
    data: {
      email?: string;
      password?: string;
      name?: string;
      role?: string;
      isActive?: boolean;
      notionPageId?: string;
    }
  ) => api.patch(`/users/${id}`, data),

  delete: (id: string) => api.delete(`/users/${id}`),

  updatePermissions: (
    id: string,
    permissions: {
      FINANCE?: boolean;
      AUTOMATIONS?: boolean;
      PEOPLE_CORE_MANAGEMENT?: boolean;
      CLIENT_OPERATIONS?: boolean;
      WHATSAPP_OPERATIONS?: boolean;
    }
  ) => api.patch(`/users/${id}/permissions`, permissions),
};

// Accesses API
export const accessesAPI = {
  list: (params?: GetAccessesParams) => api.get('/accesses', { params }),

  stats: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/accesses/stats', { params }),
};

// Operations API
export const operationsAPI = {
  addCollaboratorsToGroups: (data: { phoneNumber: string; instanceToken: string }) =>
    api.post('/operations/add-collaborators', data),
};

// Sales API
export const salesAPI = {
  list: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    saleType?: string;
    month?: number;
    year?: number;
    clientEmail?: string;
  }) => api.get('/sales', { params }),

  getMetrics: (params?: { status?: string; month?: number; year?: number }) => api.get('/sales/metrics', { params }),

  getFlightsProfit: (month?: number, year?: number) =>
    api.get('/sales/flights-profit', { params: { month, year } }),

  getByPaymentId: (paymentId: string) => api.get(`/sales/${paymentId}`),

  // Resumo anual de vendas aprovadas, mês a mês
  getAnnualSummary: (year: number) => api.get('/sales/annual-summary', { params: { year } }),
};

// Receipts API
export const receiptsAPI = {
  create: (data: {
    recipientName: string;
    recipientEmail: string;
    recipientDocument: string;
    issueDate?: string;
    items: Array<{ date: string; description: string; value: number }>;
    paymentMethod: string;
  }) => api.post('/billing/receipts', data),

  list: (params?: {
    page?: number;
    limit?: number;
    recipientDocument?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get('/billing/receipts', { params }),

  getById: (id: string) => api.get(`/billing/receipts/${id}`),

  getDownloadUrl: (id: string) => api.get(`/billing/receipts/${id}/download`),

  regenerate: (id: string) => api.post(`/billing/receipts/${id}/regenerate`),

  delete: (id: string) => api.delete(`/billing/receipts/${id}`),
};

// People Core API (CLT)
export const peopleCoreAPI = {
  // Employees
  getMyProfile: () => api.get('/people-core/employees/me'),
  updateMyProfile: (data: { dailyWorkHours?: number; timezone?: string }) =>
    api.patch('/people-core/employees/me', data),
  listEmployees: () => api.get('/people-core/employees'),
  upsertEmployee: (data: { userId: string; dailyWorkHours?: number; timezone?: string }) =>
    api.post('/people-core/employees', data),

  // Time Entry (Ponto)
  getTodayEntry: () => api.get('/people-core/time-entries/today'),
  getTimeEntries: (params?: { startDate?: string; endDate?: string; employeeId?: string }) =>
    api.get('/people-core/time-entries', { params }),
  clockIn: (data?: { clockIn?: string }) => api.post('/people-core/time-entries/clock-in', data ?? {}),
  breakStart: (data?: { breakStart?: string }) => api.post('/people-core/time-entries/break-start', data ?? {}),
  breakEnd: (data?: { breakEnd?: string }) => api.post('/people-core/time-entries/break-end', data ?? {}),
  clockOut: (data?: { clockOut?: string }) => api.post('/people-core/time-entries/clock-out', data ?? {}),
  getTimesheet: (params?: { startDate?: string; endDate?: string; employeeId?: string }) =>
    api.get('/people-core/timesheet', { params }),

  // Projects
  listProjects: (params?: { all?: string }) => api.get('/people-core/projects', { params }),
  createProject: (data: { name: string; description?: string }) =>
    api.post('/people-core/projects', data),
  updateProject: (id: string, data: { name?: string; description?: string; isActive?: boolean }) =>
    api.patch(`/people-core/projects/${id}`, data),
  deleteProject: (id: string) => api.delete(`/people-core/projects/${id}`),
  createActivity: (projectId: string, data: { name: string; description?: string }) =>
    api.post(`/people-core/projects/${projectId}/activities`, data),
  updateActivity: (id: string, data: { name?: string; description?: string; isActive?: boolean }) =>
    api.patch(`/people-core/activities/${id}`, data),
  allocateEmployee: (data: { employeeId: string; projectId: string }) =>
    api.post('/people-core/projects/allocate', data),
  deallocateEmployee: (projectId: string, employeeId: string) =>
    api.delete(`/people-core/projects/${projectId}/employees/${employeeId}`),

  // Time Allocations
  listAllocations: (params?: { startDate?: string; endDate?: string; projectId?: string; employeeId?: string }) =>
    api.get('/people-core/time-allocations', { params }),
  createAllocation: (data: { activityId: string; date: string; hours: number; description?: string; employeeId?: string }) =>
    api.post('/people-core/time-allocations', data),
  updateAllocation: (id: string, data: { hours?: number; description?: string }) =>
    api.patch(`/people-core/time-allocations/${id}`, data),
  deleteAllocation: (id: string) => api.delete(`/people-core/time-allocations/${id}`),

  // Adjustment Requests
  listAdjustmentRequests: (params?: { status?: string; startDate?: string; endDate?: string; employeeId?: string }) =>
    api.get('/people-core/adjustment-requests', { params }),
  createAdjustmentRequest: (data: {
    date: string;
    requestedData: { clockIn?: string; clockOut?: string; breakStart?: string; breakEnd?: string };
    justification: string;
  }) => api.post('/people-core/adjustment-requests', data),
  reviewAdjustmentRequest: (id: string, data: { status: 'APPROVED' | 'REJECTED'; reviewNote?: string }) =>
    api.patch(`/people-core/adjustment-requests/${id}/review`, data),

  // Audit Logs (ADMIN only)
  listAuditLogs: (params?: {
    entity?: string;
    entityId?: string;
    userId?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => api.get('/people-core/audit-logs', { params }),
  getRecentAuditLogs: (params?: { hours?: number; limit?: number }) =>
    api.get('/people-core/audit-logs/recent', { params }),
  getEntityAuditLogs: (entity: string, entityId: string) =>
    api.get(`/people-core/audit-logs/entity/${entity}/${entityId}`),
  getUserAuditLogs: (userId: string) =>
    api.get(`/people-core/audit-logs/user/${userId}`),

  // HCM - Employee Management
  listEmployeesHCM: (params?: { status?: string; search?: string; departmentId?: string; jobPositionId?: string }) =>
    api.get('/people-core/hcm/employees', { params }),
  getEmployeeHCM: (id: string) =>
    api.get(`/people-core/hcm/employees/${id}`),
  createEmployeeHCM: (data: unknown) =>
    api.post('/people-core/hcm/employees', data),
  updateEmployeeHCM: (id: string, data: unknown) =>
    api.patch(`/people-core/hcm/employees/${id}`, data),
  deleteEmployeeHCM: (id: string) =>
    api.delete(`/people-core/hcm/employees/${id}`),
  getSubordinates: (managerId: string) =>
    api.get(`/people-core/hcm/employees/${managerId}/subordinates`),

  // HCM - Departments
  listDepartments: (params?: { includeInactive?: boolean }) =>
    api.get('/people-core/hcm/departments', { params }),
  getDepartment: (id: string) =>
    api.get(`/people-core/hcm/departments/${id}`),
  createDepartment: (data: { name: string; description?: string }) =>
    api.post('/people-core/hcm/departments', data),
  updateDepartment: (id: string, data: { name?: string; description?: string; isActive?: boolean }) =>
    api.patch(`/people-core/hcm/departments/${id}`, data),
  deleteDepartment: (id: string) =>
    api.delete(`/people-core/hcm/departments/${id}`),

  // HCM - Job Positions
  listJobPositions: (params?: { departmentId?: string; includeInactive?: boolean }) =>
    api.get('/people-core/hcm/job-positions', { params }),
  getJobPosition: (id: string) =>
    api.get(`/people-core/hcm/job-positions/${id}`),
  createJobPosition: (data: { name: string; departmentId: string; baseSalary?: number }) =>
    api.post('/people-core/hcm/job-positions', data),
  updateJobPosition: (id: string, data: { name?: string; departmentId?: string; baseSalary?: number; isActive?: boolean }) =>
    api.patch(`/people-core/hcm/job-positions/${id}`, data),
  deleteJobPosition: (id: string) =>
    api.delete(`/people-core/hcm/job-positions/${id}`),

  // HCM - Salary History
  getSalaryHistory: (employeeId: string) =>
    api.get(`/people-core/hcm/employees/${employeeId}/salary-history`),
  addSalaryHistory: (employeeId: string, data: { value: number; reason?: string }) =>
    api.post(`/people-core/hcm/employees/${employeeId}/salary-history`, data),

  // HCM - Performance Insights
  getPerformanceInsights: (employeeId: string) =>
    api.get(`/people-core/hcm/employees/${employeeId}/performance-insights`),

  // HCM - Dashboard
  getHRDashboard: () =>
    api.get('/people-core/hcm/dashboard'),

  // HCM - Total Cost
  getTotalEmployeeCost: () =>
    api.get('/people-core/hcm/total-cost'),

  // Partners (Sócios)
  listPartners: () =>
    api.get('/people-core/partners'),
  getTotalProfitAdvances: () =>
    api.get('/people-core/partners/total-profit-advances'),
};

// Commissions API
export const commissionsAPI = {
  // Regras de comissão (ADMIN only)
  upsertRule: (employeeId: string, data: {
    serviceType: string;
    isNational?: boolean | null;
    level1FixedValue: number;
    level2FixedValue: number;
    level3FixedValue: number;
    level1Percentage?: number;
    level2Percentage?: number;
    level3Percentage?: number;
  }) => api.post(`/commissions/rules/${employeeId}`, data),

  getRulesByEmployee: (employeeId: string) => api.get(`/commissions/rules/${employeeId}`),

  // Registros de comissão
  getRecordsByEmployee: (employeeId: string, month?: number, year?: number) =>
    api.get(`/commissions/records/${employeeId}`, { params: { month, year } }),

  getSummary: (employeeId: string, month?: number, year?: number) =>
    api.get(`/commissions/summary/${employeeId}`, { params: { month, year } }),

  // Total para dashboard
  getTotal: () => api.get('/commissions/total'),
  getTotalByMonth: (month: number, year: number) =>
    api.get('/commissions/total-by-month', { params: { month, year } }),

  // Resumo de todos os colaboradores
  getAllEmployeesSummary: (month?: number, year?: number) =>
    api.get('/commissions/all-employees-summary', { params: { month, year } }),
};

// Operational Costs API
export const operationalCostsAPI = {
  // CRUD de custos operacionais (ADMIN only)
  create: (data: {
    title: string;
    description?: string;
    category: string;
    amount: number;
    paymentDate: string;
    paymentMethod?: string;
    notes?: string;
    isRecurring?: boolean;
    recurrenceFrequency?: 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'YEARLY';
    recurringDayOfMonth?: number;
  }) => api.post('/billing/operational-costs', data),

  list: (filters?: {
    month?: number;
    year?: number;
    category?: string;
    isRecurring?: boolean;
    parentCostId?: string;
  }) => api.get('/billing/operational-costs', { params: filters }),

  getById: (id: string) => api.get(`/billing/operational-costs/${id}`),

  update: (id: string, data: {
    title?: string;
    description?: string;
    category?: string;
    amount?: number;
    paymentDate?: string;
    paymentMethod?: string;
    notes?: string;
  }) => api.patch(`/billing/operational-costs/${id}`, data),

  delete: (id: string) => api.delete(`/billing/operational-costs/${id}`),

  // Recorrências
  listRecurring: () => api.get('/billing/operational-costs/recurring'),

  updateRecurrence: (id: string, data: {
    title: string;
    description?: string | null;
    category: string;
    amount: number;
    paymentMethod?: string | null;
    notes?: string | null;
    frequency: 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'YEARLY';
    dayOfMonth: number;
  }) => api.put(`/billing/operational-costs/recurring/${id}`, data),

  endRecurrence: (id: string, endDate: string) =>
    api.post(`/billing/operational-costs/recurring/${id}/end`, { endDate }),

  // Histórico de alterações (auditoria)
  listAudit: (params?: { days?: number; costId?: string }) =>
    api.get('/billing/operational-costs/audit', { params }),

  // Resumos (ADMIN/GESTOR)
  getSummaryByCategory: (month: number, year: number) =>
    api.get('/billing/operational-costs/summary/by-category', { params: { month, year } }),

  getTotal: (month: number, year: number) =>
    api.get('/billing/operational-costs/summary/total', { params: { month, year } }),

  // Resumo anual das despesas, mês a mês
  getAnnualSummary: (year: number) =>
    api.get('/billing/operational-costs/summary/annual', { params: { year } }),
};

// Snapshots Financeiros
export const financialSnapshotAPI = {
  getFinancialData: (month: number, year: number) =>
    api.get('/billing/financial-snapshot', { params: { month, year } }),

  saveSnapshot: (month: number, year: number) =>
    api.post('/billing/financial-snapshot/save', { month, year }),

  closeMonth: (month: number, year: number) =>
    api.post('/billing/financial-snapshot/close', { month, year }),

  reopenMonth: (month: number, year: number) =>
    api.post('/billing/financial-snapshot/reopen', { month, year }),

  listSnapshots: (filters?: { isClosed?: boolean; year?: number }) =>
    api.get('/billing/financial-snapshot/list', { params: filters }),

  getDREByYear: (year: number) =>
    api.get('/billing/financial-snapshot/dre', { params: { year } }),
};

// Caixa
export const cashBoxAPI = {
  getCashBox: () => api.get('/billing/cash-box'),
  getCutoffPreview: () => api.get('/billing/cash-box/cutoff/preview'),
  createCutoff: (data: { month: number; year: number; notes?: string }) =>
    api.post('/billing/cash-box/cutoff', data),
};

// Dívida da empresa com os sócios (caixa congelado no corte)
export interface PartnerDebtPaymentInput {
  partnerDebtId: string;
  paymentDate: string; // YYYY-MM-DD
  amount: number;
  notes?: string | null;
}

export const partnerDebtAPI = {
  getSummary: () => api.get('/billing/partner-debt'),
  createPayment: (data: PartnerDebtPaymentInput) => api.post('/billing/partner-debt/payments', data),
  updatePayment: (id: string, data: PartnerDebtPaymentInput) =>
    api.put(`/billing/partner-debt/payments/${id}`, data),
  deletePayment: (id: string) => api.delete(`/billing/partner-debt/payments/${id}`),
};

// Suggestions API
export const suggestionsAPI = {
  // Criar sugestão (todos os usuários autenticados)
  create: (data: {
    title: string;
    description: string;
    type: 'SUGGESTION' | 'PROBLEM';
    module: string;
  }) => api.post('/suggestions', data),

  // Listar sugestões
  // - Usuários comuns: vêem apenas suas próprias sugestões
  // - Admin: vê todas e pode filtrar por userId
  list: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: 'SUGGESTION' | 'PROBLEM';
    userId?: string;
  }) => api.get('/suggestions', { params }),

  // Buscar sugestão por ID
  getById: (id: string) => api.get(`/suggestions/${id}`),

  // Atualizar status (apenas admin)
  updateStatus: (id: string, status: string, conclusion?: string) =>
    api.patch(`/suggestions/${id}/status`, { status, conclusion }),

  // Deletar sugestão
  delete: (id: string) => api.delete(`/suggestions/${id}`),
};

// Incident Management API (Central de Problemas)
export const incidentManagementAPI = {
  // ==========================================
  // INCIDENTS
  // ==========================================
  createIncident: (data: {
    employeeId: string;
    categoryId: string;
    description: string;
    financialLoss?: number;
  }) => api.post('/incident-management/incidents', data),

  listIncidents: (params?: {
    page?: number;
    limit?: number;
    employeeId?: string;
    categoryId?: string;
    status?: string;
    appliedDegree?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get('/incident-management/incidents', { params }),

  getIncidentById: (id: string) => api.get(`/incident-management/incidents/${id}`),

  updateIncident: (id: string, data: {
    description?: string;
    financialLoss?: number;
    monitoringDeadline?: string;
  }) => api.patch(`/incident-management/incidents/${id}`, data),

  approveIncident: (id: string, data: {
    appliedDegree: string;
  }) => api.post(`/incident-management/incidents/${id}/approve`, data),

  cancelIncident: (id: string, reason?: string) =>
    api.delete(`/incident-management/incidents/${id}`, { data: { reason } }),

  getEmployeeHistory: (employeeId: string) =>
    api.get(`/incident-management/incidents/employee/${employeeId}/history`),

  calculateDegree: (employeeId: string) =>
    api.get(`/incident-management/incidents/employee/${employeeId}/calculate-degree`),

  getStatistics: (params?: {
    startDate?: string;
    endDate?: string;
    employeeId?: string;
    categoryId?: string;
  }) => api.get('/incident-management/statistics', { params }),

  // ==========================================
  // CREDIT BALANCE
  // ==========================================
  getCreditBalance: (employeeId: string) =>
    api.get(`/incident-management/credit-balance/${employeeId}`),

  // ==========================================
  // CATEGORIES
  // ==========================================
  createCategory: (data: { name: string; description?: string }) =>
    api.post('/incident-management/categories', data),

  listCategories: () => api.get('/incident-management/categories'),

  getCategoryById: (id: string) => api.get(`/incident-management/categories/${id}`),

  updateCategory: (id: string, data: { name?: string; description?: string }) =>
    api.patch(`/incident-management/categories/${id}`, data),

  deactivateCategory: (id: string) => api.delete(`/incident-management/categories/${id}`),

  // ==========================================
  // WARNING DOCUMENTS
  // ==========================================
  getDocumentById: (id: string) => api.get(`/incident-management/warning-documents/${id}`),

  signDocument: (id: string) =>
    api.post(`/incident-management/warning-documents/${id}/sign`, {}),

  getPendingDocuments: (employeeId: string) =>
    api.get(`/incident-management/warning-documents/employee/${employeeId}/pending`),

  updatePdfUrl: (id: string, pdfUrl: string) =>
    api.patch(`/incident-management/warning-documents/${id}/pdf-url`, { pdfUrl }),

  // ==========================================
  // PAYMENT AGREEMENTS
  // ==========================================
  createAgreement: (data: {
    incidentId: string;
    installments: number;
    totalAmount?: number;
    startDate?: string;
    notes?: string;
  }) => api.post('/incident-management/payment-agreements', data),

  getAgreementById: (id: string) => api.get(`/incident-management/payment-agreements/${id}`),

  updateAgreement: (id: string, data: {
    paidInstallments?: number;
    notes?: string;
  }) => api.patch(`/incident-management/payment-agreements/${id}`, data),

  registerPayment: (id: string) =>
    api.post(`/incident-management/payment-agreements/${id}/payment`),

  getActiveAgreementsByEmployee: (employeeId: string) =>
    api.get(`/incident-management/payment-agreements/employee/${employeeId}/active`),

  getAllActiveAgreements: () =>
    api.get('/incident-management/payment-agreements/active'),

  cancelAgreement: (id: string, reason?: string) =>
    api.delete(`/incident-management/payment-agreements/${id}`, { data: { reason } }),

  getAgreementSituation: (id: string) =>
    api.get(`/incident-management/payment-agreements/${id}/situation`),

  getAllPendingAgreements: () =>
    api.get('/incident-management/payment-agreements/pending'),

  activatePendingAgreement: (id: string, data: {
    installments: number;
    startDate: string;
    notes?: string;
  }) => api.post(`/incident-management/payment-agreements/${id}/activate`, data),

  // ==========================================
  // RESET REQUESTS
  // ==========================================
  createResetRequest: (employeeId: string) =>
    api.post('/incident-management/reset-requests', { employeeId }),

  listResetRequests: (params?: {
    page?: number;
    limit?: number;
    employeeId?: string;
    status?: string;
  }) => api.get('/incident-management/reset-requests', { params }),

  getResetRequestById: (id: string) =>
    api.get(`/incident-management/reset-requests/${id}`),

  reviewResetRequest: (id: string, data: {
    status: 'APPROVED' | 'REJECTED';
    managerNotes?: string;
  }) => api.post(`/incident-management/reset-requests/${id}/review`, data),

  getEligibleEmployees: () =>
    api.get('/incident-management/reset-requests/eligible-employees'),

  // ==========================================
  // APPROVERS
  // ==========================================
  listApprovers: () => api.get('/incident-management/approvers'),

  addApprover: (userId: string) =>
    api.post('/incident-management/approvers', { userId }),

  removeApprover: (userId: string) =>
    api.delete(`/incident-management/approvers/${userId}`),

  checkIsApprover: (userId: string) =>
    api.get(`/incident-management/approvers/check/${userId}`),

  getAvailableUsers: () =>
    api.get('/incident-management/approvers/available-users'),
};

// Instagram
export const instagramAPI = {
  generatePosts: (profileId: string, count: 3 | 5 | 10, theme?: string) =>
    api.post('/instagram/generate-posts', { profileId, count, theme }),
};

// Employee Payments API
export const employeePaymentsAPI = {
  list: (month?: number, year?: number) =>
    api.get('/billing/employee-payments', { params: { month, year } }),

  listBenefits: (month?: number, year?: number) =>
    api.get('/billing/employee-payments/benefits', { params: { month, year } }),

  getById: (employeeId: string) =>
    api.get(`/billing/employee-payments/${employeeId}`),

  generatePixQRCode: (employeeId: string, amount: number, description?: string) =>
    api.post(`/billing/employee-payments/${employeeId}/pix-qrcode`, { amount, description }),

  markAsPaid: (employeeId: string, month: number, year: number, notes?: string) =>
    api.patch(`/billing/employee-payments/${employeeId}/mark-paid`, { month, year, notes }),

  markAsUnpaid: (employeeId: string, month: number, year: number) =>
    api.patch(`/billing/employee-payments/${employeeId}/mark-unpaid`, { month, year }),
};

// Adicionais a pagar lançados à mão (somam no pagamento do mês como comissão)
export const employeeAdditionalsAPI = {
  list: (month: number, year: number, employeeId?: string) =>
    api.get('/billing/employee-additionals', { params: { month, year, employeeId } }),

  create: (data: { employeeId: string; month: number; year: number; amount: number; description: string }) =>
    api.post('/billing/employee-additionals', data),

  update: (id: string, data: { amount?: number; description?: string }) =>
    api.put(`/billing/employee-additionals/${id}`, data),

  remove: (id: string) => api.delete(`/billing/employee-additionals/${id}`),
};

// Operational Metrics API
export const operationalMetricsAPI = {
  getMetrics: (month: number, year: number) =>
    api.get('/operations/metrics', { params: { month, year } }),
};

// AI Usage API (ADMIN only)
export const aiUsageAPI = {
  getSummary: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/ai-usage/summary', { params }),

  getByModule: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/ai-usage/by-module', { params }),

  getByModel: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/ai-usage/by-model', { params }),

  getByOperation: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/ai-usage/by-operation', { params }),

  getDailyCosts: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/ai-usage/daily', { params }),

  getTopExpensive: (params?: { limit?: number; startDate?: string; endDate?: string }) =>
    api.get('/ai-usage/top-expensive', { params }),

  getModuleCost: (module: string, params?: { startDate?: string; endDate?: string }) =>
    api.get(`/ai-usage/module/${module}`, { params }),
};

// ==========================================
// Client Onboarding API
// ==========================================
// Endpoints do novo módulo client-onboarding.
// Requer permissão CLIENT_OPERATIONS.
export const onboardingCasesAPI = {
  getStats: () => api.get('/onboarding/stats'),

  list: (params?: {
    stage?: string | string[];
    search?: string;
    createdFrom?: string;
    createdTo?: string;
    skip?: number;
    take?: number;
  }) => api.get('/onboarding/cases', { params }),

  getById: (id: string) => api.get(`/onboarding/cases/${id}`),

  retryAiAnalysis: (id: string, force = false) =>
    api.post(`/onboarding/cases/${id}/retry-ai-analysis`, { force }),

  sendCallLink: (id: string) =>
    api.post(`/onboarding/cases/${id}/send-call-link`),
};
